import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { IAssetsContainerProps, AssetProps } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { Asset } from "app/pages/digital-suite/home-page/components/CustomAssets/Assets/Asset/Asset";
import { GridsContainer, GridsTable } from "app/pages/digital-suite/home-page/components/CustomAssets/styledComponents";
import { BlankGrid } from "app/pages/digital-suite/home-page/components/CustomAssets/Containers/AssetsContainer/styledComponents";

interface IPopulatedStateContainerProps extends IAssetsContainerProps<AssetProps> {
    maxRows: number;
}

export const AssetsContainer: React.FC<IPopulatedStateContainerProps> = ({ columns, maxRows, assets }) => {
    const gridsGap = 8;
    const tableRef = useRef<HTMLDivElement>(null);
    const [tableWidth, setTableWidth] = useState<number>(0);
    const gridWidth = useMemo<number>(() => tableWidth / columns - gridsGap, [tableWidth, columns]);
    const displayedAssets = useMemo<Array<AssetProps>>(() => {
        return assets.slice(0, maxRows * columns);
    }, [assets, columns, maxRows]);

    const emptyGridsAmount = useMemo<number>(() => {
        const maxGrids = maxRows * columns;
        const unusedGrids = maxGrids - displayedAssets.length;
        return unusedGrids % columns;
    }, [columns, maxRows, displayedAssets]);

    useEffect(() => {
        const adjustTableWidth = (): void => setTableWidth(tableRef?.current?.clientWidth ?? 0);

        adjustTableWidth();
        window.addEventListener("resize", adjustTableWidth);

        return () => {
            window.removeEventListener("resize", adjustTableWidth);
        };
    }, [tableRef?.current]);

    return (
        <GridsContainer ref={tableRef}>
            <GridsTable
                columns={columns}
                gridsGap={gridsGap}
            >
                {displayedAssets.map((asset, index) => (
                    <Asset
                        {...asset}
                        key={asset.id}
                        index={index}
                        maxWidth={gridWidth}
                    />
                ))}
                {[...Array(emptyGridsAmount)].map((_, index) => (
                    <BlankGrid key={index} />
                ))}
            </GridsTable>
        </GridsContainer>
    );
};
